import { get } from 'lodash';
import cookie from 'cookie';
import uuidv4 from 'uuid/v4';
import { GTM } from './GTM';

// setup create provider array to be interacted with when firing off tags/events
const providers = [GTM];

/**
 * Function to track any event via object which is defined by the analytics team
 *
 * @param {object} payload object containing the key/values expected by analytics reporting
 */
export const trackEvent = (payload) => {
  let analyticIDs = {};

  if (process.browser) {
    const { LLV2_CLIENT_ID, LLV2_SESSION_ID } = cookie.parse(document.cookie || '');

    analyticIDs = {
      LLV2_CLIENT_ID,
      LLV2_SESSION_ID
    };

    // only add to payload if there is something to add.
    if (LLV2_CLIENT_ID || LLV2_SESSION_ID || (LLV2_CLIENT_ID && LLV2_SESSION_ID)) {
      payload.analyticIDs = analyticIDs;
    }
  }

  providers.forEach((provider) => {
    provider.trackEvent(payload);
  });
};

/**
 * preset structure for tracking routes.
 *
 * @param {string} payload string name of the page.
 */
export const trackRoute = (payload) => {
  let analyticIDs = {};

  if (process.browser) {
    const { LLV2_CLIENT_ID, LLV2_SESSION_ID } = cookie.parse(document.cookie || '');

    analyticIDs = {
      LLV2_CLIENT_ID,
      LLV2_SESSION_ID
    };
  }

  providers.forEach((provider) => {
    provider.trackEvent({
      event: 'pageView',
      pageName: payload,
      analyticIDs
    });
  });
};

const COOKIE_VARS = {
  client: 'LLV2_CLIENT_ID',
  session: 'LLV2_SESSION_ID'
};

/**
 * Checks if we have cookie info for analytics, if not creates the info and cookie
 *
 * @static
 * @param {object} ctx
 * @returns {object} A locale object containing LLV2_CLIENT_ID and LLV2_SESSION_ID.
 * @memberof LodgeLinkApp
 */
export const getAnalyticCookiesFromContext = (ctx) => {
  const { res } = ctx;
  const cookiesToSet = [];
  const cookieValues = {};
  // determine how to get the cookie info.
  let cookieRef = '';

  if (!process.browser) {
    cookieRef = get(ctx, 'req.headers.cookie', '');
  } else {
    cookieRef = get(document, 'cookie', '');
  }

  const sessionUUID = uuidv4();
  // check the headers and see if we have any cookies already
  const { LLV2_CLIENT_ID, LLV2_SESSION_ID } = cookie.parse(cookieRef);
  const sessionExpireTime = new Date();
  sessionExpireTime.setTime(sessionExpireTime.getTime() + 30 * 60 * 1000);

  // check if we have a client cookie, and if we don't set it. should always exist unless user clears cookies.
  if (!LLV2_CLIENT_ID) {
    const clientUUID = uuidv4();
    const clientExpireTime = new Date();
    clientExpireTime.setFullYear(3050, 1);
    cookiesToSet.push(cookie.serialize(COOKIE_VARS.client, clientUUID, { expires: clientExpireTime, path: '/' }));

    cookieValues.LLV2_CLIENT_ID = clientUUID;
  } else {
    cookieValues.LLV2_CLIENT_ID = LLV2_CLIENT_ID;
  }

  // check for session cookie. if is there take value and update expirary, if not set it.
  if (!LLV2_SESSION_ID) {
    cookiesToSet.push(cookie.serialize(COOKIE_VARS.session, sessionUUID, { expires: sessionExpireTime, path: '/' }));
    cookieValues.LLV2_SESSION_ID = sessionUUID;
  } else {
    cookiesToSet.push(
      cookie.serialize(COOKIE_VARS.session, LLV2_SESSION_ID, { expires: sessionExpireTime, path: '/' })
    );
    cookieValues.LLV2_SESSION_ID = LLV2_SESSION_ID;
  }

  // if we need to update/set cookies do it. have to do it all at once or only last set works...
  if (ctx && res && cookiesToSet.length > 0) {
    ctx.res.setHeader('Set-Cookie', cookiesToSet);
  } else if (cookiesToSet.length > 0 && process.browser) {
    cookiesToSet.forEach((newCookie) => {
      document.cookie = newCookie;
    });
  }

  return cookieValues;
};
